import { FC, DragEvent, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Alert,
  Button,
  Label,
  Modal,
  Select,
  Spinner,
  Textarea,
  TextInput,
} from "flowbite-react";
import { HiOutlineCloudUpload, HiX } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { AIAgentCreatePayload } from "@/api/companyApi/aiAgent";
import {
  AI_AGENT_TONES,
  CHAT_HISTORY_UNITS,
  KB_FILE_EXTENSIONS,
  LLM_OPTIONS,
} from "@/constants/aiAgent";

interface FileUploadFieldProps {
  label: string;
  file?: File;
  onSelect: (file?: File) => void;
}

const hasSupportedExtension = (file: File) =>
  KB_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const FileUploadField: FC<FileUploadFieldProps> = ({ label, file, onSelect }) => {
  const { t } = useTranslation(["companySetting"]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [typeError, setTypeError] = useState(false);

  // Some browsers report an empty MIME type for .csv/.xlsx, so the extension is
  // the reliable check here.
  const pickFile = (files: FileList | null) => {
    if (!files?.length) return;
    const candidate = Array.from(files).find(hasSupportedExtension);
    if (!candidate) {
      setTypeError(true);
      return;
    }
    setTypeError(false);
    onSelect(candidate);
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files);
  };

  const handleRemove = () => {
    onSelect(undefined);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <Label value={label} />
      {file ? (
        <div className="mt-1 flex items-center justify-between rounded-md border border-green-200 bg-green-50 px-4 py-2 dark:border-green-900 dark:bg-green-900/30">
          <span className="max-w-[85%] truncate text-sm text-green-800 dark:text-green-300">
            {file.name}
          </span>
          <button
            type="button"
            onClick={handleRemove}
            aria-label={t("companySetting:aiAgent.create.removeFile")}
            className="text-green-700 hover:text-green-900 dark:text-green-300"
          >
            <HiX className="size-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="mt-1 w-full cursor-pointer rounded-md border-2 border-dashed border-gray-300 p-6 text-center transition hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
        >
          <HiOutlineCloudUpload className="mx-auto size-6 text-gray-400" />
          <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("companySetting:aiAgent.create.dropzone")}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {t("companySetting:aiAgent.create.dropzoneHint")}
          </p>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={KB_FILE_EXTENSIONS.join(",")}
        className="hidden"
        onChange={(e) => pickFile(e.target.files)}
      />
      {typeError && (
        <p className="mt-1 text-sm text-red-600">
          {t("companySetting:aiAgent.create.unsupportedFile")}
        </p>
      )}
    </div>
  );
};

interface AIAgentCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AIAgentCreatePayload) => void;
  isSubmitting: boolean;
  errorMessage?: string;
}

const defaultValues: AIAgentCreatePayload = {
  display_name: "",
  company_agent_tone: "",
  chosen_llm: LLM_OPTIONS[0].value,
  company_scope: "",
  ai_greeting_text: "",
  chat_history_update_value: 1,
  chat_history_update_unit: "month",
};

const AIAgentCreateDialog: FC<AIAgentCreateDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  errorMessage,
}) => {
  const { t } = useTranslation(["companySetting", "common"]);
  // Step 1 holds the configuration, step 2 the optional training files, the
  // same split the gateway console uses.
  const [step, setStep] = useState(1);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<AIAgentCreatePayload>({ defaultValues });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setStep(1);
    }
  }, [open, reset]);

  const goToFiles = async () => {
    const valid = await trigger([
      "display_name",
      "company_agent_tone",
      "chosen_llm",
      "company_scope",
      "chat_history_update_value",
      "chat_history_update_unit",
    ]);
    if (valid) setStep(2);
  };

  return (
    <Modal show={open} onClose={onClose} size="2xl">
      <Modal.Header>{t("companySetting:aiAgent.create.title")}</Modal.Header>
      <Modal.Body>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          {step === 1
            ? t("companySetting:aiAgent.create.stepConfig")
            : t("companySetting:aiAgent.create.stepFiles")}
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {step === 1 ? (
            <>
              <div>
                <Label
                  htmlFor="display_name"
                  value={t("companySetting:aiAgent.create.nameLabel")}
                />
                <TextInput
                  id="display_name"
                  placeholder={t("companySetting:aiAgent.create.namePlaceholder")}
                  {...register("display_name", {
                    required: t("companySetting:required") as string,
                    maxLength: {
                      value: 100,
                      message: t("companySetting:aiAgent.create.nameMaxLength"),
                    },
                  })}
                  color={errors.display_name ? "failure" : undefined}
                  className="mt-1"
                />
                {errors.display_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.display_name.message}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="company_agent_tone"
                  value={t("companySetting:aiAgent.create.toneLabel")}
                />
                <Controller
                  name="company_agent_tone"
                  control={control}
                  rules={{ required: t("companySetting:required") as string }}
                  render={({ field }) => (
                    <Select
                      id="company_agent_tone"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      color={errors.company_agent_tone ? "failure" : undefined}
                      className="mt-1"
                    >
                      <option value="">
                        {t("companySetting:aiAgent.create.tonePlaceholder")}
                      </option>
                      {AI_AGENT_TONES.map((tone) => (
                        <option key={tone.value} value={tone.value}>
                          {t(tone.labelKey)}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                {errors.company_agent_tone && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_agent_tone.message}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="create_chosen_llm"
                  value={t("companySetting:aiAgent.llmLabel")}
                />
                <Controller
                  name="chosen_llm"
                  control={control}
                  rules={{ required: t("companySetting:required") as string }}
                  render={({ field }) => (
                    <Select
                      id="create_chosen_llm"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      color={errors.chosen_llm ? "failure" : undefined}
                      className="mt-1"
                    >
                      {LLM_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("companySetting:aiAgent.llmHelp")}
                </p>
              </div>

              <div>
                <Label
                  htmlFor="create_company_scope"
                  value={t("companySetting:aiAgent.scopeLabel")}
                />
                <Textarea
                  id="create_company_scope"
                  rows={6}
                  placeholder={t("companySetting:aiAgent.scopePlaceholder")}
                  {...register("company_scope", {
                    required: t("companySetting:required") as string,
                    maxLength: {
                      value: 5000,
                      message: t("companySetting:aiAgent.scopeMaxLength"),
                    },
                  })}
                  color={errors.company_scope ? "failure" : undefined}
                  className="mt-1"
                />
                {errors.company_scope && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_scope.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("companySetting:aiAgent.scopeHelp")}
                </p>
              </div>

{/* Greeting Message gizlendi (comment out)
              <div>
                <Label
                  htmlFor="create_ai_greeting_text"
                  value={t("companySetting:aiAgent.greetingLabel")}
                />
                <Textarea
                  id="create_ai_greeting_text"
                  rows={3}
                  placeholder={t("companySetting:aiAgent.greetingPlaceholder")}
                  {...register("ai_greeting_text", {
                    maxLength: {
                      value: 1000,
                      message: t("companySetting:aiAgent.greetingMaxLength"),
                    },
                  })}
                  color={errors.ai_greeting_text ? "failure" : undefined}
                  className="mt-1"
                />
                {errors.ai_greeting_text && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.ai_greeting_text.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("companySetting:aiAgent.greetingHelp")}
                </p>
              </div>
*/}

              <div>
                <Label value={t("companySetting:aiAgent.create.frequencyLabel")} />
                <div className="mt-1 grid grid-cols-2 gap-4">
                  <TextInput
                    id="chat_history_update_value"
                    type="number"
                    min={1}
                    placeholder={t("companySetting:aiAgent.create.valuePlaceholder")}
                    {...register("chat_history_update_value", {
                      valueAsNumber: true,
                      required: t("companySetting:required") as string,
                      min: {
                        value: 1,
                        message: t("companySetting:aiAgent.create.valueMin"),
                      },
                    })}
                    color={errors.chat_history_update_value ? "failure" : undefined}
                  />
                  <Controller
                    name="chat_history_update_unit"
                    control={control}
                    rules={{ required: t("companySetting:required") as string }}
                    render={({ field }) => (
                      <Select
                        id="chat_history_update_unit"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        color={errors.chat_history_update_unit ? "failure" : undefined}
                      >
                        {CHAT_HISTORY_UNITS.map((unit) => (
                          <option key={unit.value} value={unit.value}>
                            {t(unit.labelKey)}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </div>
                {(errors.chat_history_update_value ||
                  errors.chat_history_update_unit) && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.chat_history_update_value?.message ||
                      errors.chat_history_update_unit?.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t("companySetting:aiAgent.create.frequencyHelp")}
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("companySetting:aiAgent.create.filesDescription")}
              </p>
              <FileUploadField
                label={t("companySetting:aiAgent.create.faqFiles")}
                file={watch("faq_files_path")}
                onSelect={(file) => setValue("faq_files_path", file)}
              />
              <FileUploadField
                label={t("companySetting:aiAgent.create.issueFiles")}
                file={watch("issue_resolution_files_path")}
                onSelect={(file) => setValue("issue_resolution_files_path", file)}
              />
              <FileUploadField
                label={t("companySetting:aiAgent.create.chatHistoryFile")}
                file={watch("chat_history_source_path")}
                onSelect={(file) => setValue("chat_history_source_path", file)}
              />
              {errorMessage && <Alert color="failure">{errorMessage}</Alert>}
            </>
          )}

          <div className="flex justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
            {step === 1 ? (
              <>
                <Button color="gray" onClick={onClose} type="button">
                  {t("common:cancel")}
                </Button>
                <Button color="blue" onClick={goToFiles} type="button">
                  {t("companySetting:aiAgent.create.next")}
                </Button>
              </>
            ) : (
              <>
                <Button color="gray" onClick={() => setStep(1)} type="button">
                  {t("companySetting:aiAgent.create.back")}
                </Button>
                <Button color="blue" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      {t("companySetting:aiAgent.create.submitting")}
                    </>
                  ) : (
                    t("companySetting:aiAgent.create.finish")
                  )}
                </Button>
              </>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AIAgentCreateDialog;
